import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import type { IUser } from "@/types/(my-space)/users.interface";

import { buildAccountDetails, buildQuickLinks } from "./profile-drawer.data";
import type { IProfileDetail, IProfileQuickLink } from "./types";
import {
  useSubscriptionSummary,
  type SubscriptionSummary,
} from "../useSubscriptionSummary";

export interface ProfileDrawerState {
  /** `null` until the profile request resolves — render the skeleton then. */
  user: IUser | null;
  details: IProfileDetail[];
  quickLinks: IProfileQuickLink[];
  subscription: SubscriptionSummary;
  /** Navigates, closing the drawer first so the transition isn't hidden behind it. */
  goTo: (path: string) => void;
}

/**
 * All of the profile drawer's state in one place: the signed-in user, the
 * derived row/link lists, and the shared subscription summary.
 *
 * The subscription data comes from `useSubscriptionSummary` — the same hook the
 * desktop header uses — so the drawer reads from that cache instead of issuing
 * its own (differently-keyed) duplicate requests.
 */
export const useProfileDrawer = (onClose: () => void): ProfileDrawerState => {
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "company", "nav"]);
  const { user } = useAppSelector(selectUser);
  const subscription = useSubscriptionSummary();

  const details = useMemo(
    () => (user ? buildAccountDetails(user, t) : []),
    [user, t],
  );
  const quickLinks = useMemo(
    () => (user ? buildQuickLinks(user, t) : []),
    [user, t],
  );

  const goTo = useCallback(
    (path: string) => {
      onClose();
      navigate(path);
    },
    [navigate, onClose],
  );

  return { user, details, quickLinks, subscription, goTo };
};
